/**
 * <outfit-carousel> — auto-rotating outfit photos with click-to-jump dots.
 *
 * Behavior:
 *  - Auto-advances by data-rotate-ms (default 5000ms) via setInterval
 *  - Pauses on hover and on focus-within (keyboard users)
 *  - Honors prefers-reduced-motion → no auto-rotate, only manual via dots
 *  - Click on dot jumps to that frame and restarts the timer
 */
class OutfitCarousel extends HTMLElement {
  connectedCallback() {
    this.frames = Array.from(this.querySelectorAll('[data-frame-index]'));
    this.dots = Array.from(this.querySelectorAll('[data-jump-to]'));
    this.activeIndex = 0;
    this.rotateMs = parseInt(this.dataset.rotateMs, 10) || 5000;
    this.timer = null;

    if (this.frames.length < 2) return;

    this.dots.forEach((dot, i) =>
      dot.addEventListener('click', () => {
        this.goTo(i);
        this.restart();
      })
    );

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.start();
    this.addEventListener('mouseenter', () => this.stop());
    this.addEventListener('mouseleave', () => this.start());
    this.addEventListener('focusin', () => this.stop());
    this.addEventListener('focusout', () => this.start());
  }

  disconnectedCallback() {
    this.stop();
  }

  start() {
    this.stop();
    this.timer = setInterval(() => this.advance(), this.rotateMs);
  }

  stop() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  restart() {
    this.stop();
    this.start();
  }

  advance() {
    this.goTo((this.activeIndex + 1) % this.frames.length);
  }

  goTo(index) {
    if (index === this.activeIndex) return;
    this.frames[this.activeIndex].removeAttribute('data-active');
    if (this.dots[this.activeIndex]) this.dots[this.activeIndex].removeAttribute('data-active');
    this.activeIndex = index;
    this.frames[index].setAttribute('data-active', 'true');
    if (this.dots[index]) this.dots[index].setAttribute('data-active', 'true');
  }
}

if (!customElements.get('outfit-carousel')) {
  customElements.define('outfit-carousel', OutfitCarousel);
}

/**
 * <product-scroller> — horizontal product scroller with progress thumb.
 *
 * Renders a thin progress bar underneath the scroll container; thumb width
 * mirrors the visible-to-total ratio and translateX tracks scrollLeft so it
 * reads like a custom scrollbar without the OS chrome.
 *
 * Hides the bar entirely when content fits — no scrolling needed = no UI.
 */
class ProductScroller extends HTMLElement {
  connectedCallback() {
    this.scroller = this.querySelector('[data-scroller]');
    this.thumb = this.querySelector('[data-scroll-thumb]');
    this.bar = this.thumb && this.thumb.parentElement;
    if (!this.scroller || !this.thumb) return;

    this._update = this.update.bind(this);
    this.scroller.addEventListener('scroll', this._update, { passive: true });

    if ('ResizeObserver' in window) {
      this._ro = new ResizeObserver(this._update);
      this._ro.observe(this.scroller);
      const track = this.scroller.firstElementChild;
      if (track) this._ro.observe(track);
    } else {
      window.addEventListener('resize', this._update);
    }

    requestAnimationFrame(this._update);
  }

  disconnectedCallback() {
    if (this.scroller) this.scroller.removeEventListener('scroll', this._update);
    if (this._ro) this._ro.disconnect();
    window.removeEventListener('resize', this._update);
  }

  update() {
    const { scrollWidth, clientWidth, scrollLeft } = this.scroller;
    const overflow = scrollWidth - clientWidth;

    if (overflow <= 1) {
      if (this.bar) this.bar.style.display = 'none';
      return;
    }
    if (this.bar) this.bar.style.display = '';

    const visibleRatio = clientWidth / scrollWidth;
    const thumbPct = Math.max(visibleRatio * 100, 12); // floor so the thumb stays grabbable
    const trackPx = this.bar.clientWidth;
    const thumbPx = trackPx * (thumbPct / 100);
    const offset = (scrollLeft / overflow) * (trackPx - thumbPx);

    this.thumb.style.width = `${thumbPct}%`;
    this.thumb.style.transform = `translateX(${offset}px)`;
  }
}

if (!customElements.get('product-scroller')) {
  customElements.define('product-scroller', ProductScroller);
}

/**
 * Buy-the-whole-outfit modal controller.
 *
 * A trigger [data-outfit-open="i"] opens the dialog [data-outfit-modal="i"].
 * Each product row carries size pills with a variant id + price; the user picks
 * one size per product, the total updates live, and "Add" sends every chosen
 * variant to the cart in a single /cart/add.js call, then hands off to
 * <cart-drawer>. Variant data is server-rendered — no product fetch here.
 */
(function () {
  'use strict';

  const t = (window.theme && window.theme.t) || {};
  const storefrontRoot = () =>
    (window.Shopify && window.Shopify.routes && window.Shopify.routes.root)
    || (window.theme && window.theme.routes && window.theme.routes.root)
    || '/';
  const publishCartEvent = (name, payload) => {
    if (typeof window.CustomEvent !== 'function') return;
    document.dispatchEvent(new window.CustomEvent('ev:commerce', {
      detail: { name, payload },
    }));
  };
  // Shared formatter: presentment currency (Shopify Markets) + right decimals per
  // currency, so the outfit total matches the row prices and the PDP price instead
  // of Intl's 2-decimal default (which printed "5 897,00 Kč" next to "5 897 Kč").
  const formatMoney = (window.theme && window.theme.formatMoney)
    || ((c) => new Intl.NumberFormat((window.theme && window.theme.locale) || 'cs-CZ', {
      style: 'currency',
      currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || (window.theme && window.theme.currency) || 'CZK',
    }).format((c || 0) / 100));
  let openModal = null;
  let lastTrigger = null;

  function focusable(modal) {
    return Array.from(modal.querySelectorAll(
      'button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
    )).filter((el) => el.offsetParent !== null);
  }

  function open(modal, trigger) {
    if (!modal) return;
    // Only one outfit modal at a time — close any other before opening.
    if (openModal && openModal !== modal) close(openModal, true);
    // Portal to <body>: the section uses data-reveal (a transform) which makes it
    // a containing block for position:fixed, trapping the overlay inside the section.
    // Moving the dialog to <body> guarantees a true full-viewport overlay.
    if (modal.parentElement !== document.body) document.body.appendChild(modal);
    modal.setAttribute('data-open', 'true');
    modal.setAttribute('aria-hidden', 'false');
    window.theme.lockScroll('outfit');
    openModal = modal;
    lastTrigger = trigger || null;
    sync(modal);
    const first = modal.querySelector('.otf-pill:not(:disabled)') || modal.querySelector('[data-otf-close]');
    if (first) first.focus();
  }

  function close(modal, skipRefocus) {
    if (!modal) return;
    modal.setAttribute('data-open', 'false');
    modal.setAttribute('aria-hidden', 'true');
    window.theme.unlockScroll('outfit');
    if (openModal === modal) openModal = null;
    if (lastTrigger && !skipRefocus) lastTrigger.focus();
    if (!skipRefocus) lastTrigger = null;
  }

  // A row is satisfied when it has a selected pill that is actually buyable.
  function selectedPill(row) {
    const p = row.querySelector('.otf-pill[aria-pressed="true"]');
    return p && !p.disabled ? p : null;
  }

  // Sold-out rows (no buyable variant) are excluded from the gate and the total —
  // one sold-out piece must not make the whole outfit un-addable.
  function buyableRows(modal) {
    return Array.from(modal.querySelectorAll('[data-otf-row]:not([data-otf-soldout])'));
  }

  function sync(modal) {
    const rows = buyableRows(modal);
    let total = 0;
    let selected = 0;
    rows.forEach((row) => {
      const pill = selectedPill(row);
      if (pill) { total += parseInt(pill.dataset.price, 10) || 0; selected += 1; }
    });
    // Partial outfits allowed — enabled as soon as at least one piece has a size.
    const ready = selected > 0;
    const totalEl = modal.querySelector('[data-otf-total]');
    if (totalEl) totalEl.textContent = total > 0 ? formatMoney(total) : '—';
    const addBtn = modal.querySelector('[data-otf-add]');
    const label = modal.querySelector('[data-otf-add-label]');
    if (addBtn) {
      addBtn.disabled = !ready;
      addBtn.removeAttribute('data-done');
      addBtn.classList.remove('otf-modal__add--error');
    }
    if (label) {
      label.textContent = ready
        ? `${t.outfit_add || 'Add the look'} · ${formatMoney(total)}`
        : (t.outfit_pick_sizes || 'Select all sizes');
    }
  }

  function pickSize(pill) {
    const row = pill.closest('[data-otf-row]');
    if (!row) return;
    row.querySelectorAll('.otf-pill').forEach((p) => p.setAttribute('aria-pressed', 'false'));
    pill.setAttribute('aria-pressed', 'true');
    sync(pill.closest('.otf-modal'));
  }

  async function addOutfit(modal, btn) {
    const items = buyableRows(modal)
      .map((row) => selectedPill(row))
      .filter(Boolean)
      .map((pill) => ({ id: parseInt(pill.dataset.otfVariant, 10), quantity: 1 }));
    if (!items.length) return;

    btn.disabled = true;
    btn.classList.remove('otf-modal__add--error');
    const label = btn.querySelector('[data-otf-add-label]');
    const payload = { item_count: items.length, source: 'shop_the_outfit' };
    publishCartEvent('add_to_cart_attempt', payload);
    try {
      const res = await fetch(`${storefrontRoot()}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) throw new Error('Add outfit failed');
      publishCartEvent('add_to_cart_success', payload);
      btn.setAttribute('data-done', 'true');
      if (label) label.textContent = t.outfit_added || 'Added to bag';

      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.refresh === 'function') {
        await drawer.refresh();
        close(modal, true);        // drawer takes over focus — don't bounce to the trigger
        drawer.open();
      } else {
        window.location.href = (window.theme && window.theme.routes && window.theme.routes.cart) || '/cart';
      }
    } catch (err) {
      console.error(err);
      btn.disabled = false;
      btn.removeAttribute('data-done');
      btn.classList.add('otf-modal__add--error');
      if (label) label.textContent = t.outfit_error || 'Something went wrong, try again';
    }
  }

  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-outfit-open]');
    if (trigger) {
      e.preventDefault();
      const idx = trigger.getAttribute('data-outfit-open');
      open(document.querySelector(`[data-outfit-modal="${CSS.escape(idx)}"]`), trigger);
      return;
    }
    if (e.target.closest('[data-otf-close]')) {
      close(e.target.closest('.otf-modal'));
      return;
    }
    const pill = e.target.closest('.otf-pill');
    if (pill && !pill.disabled && !pill.classList.contains('otf-pill--single')) {
      pickSize(pill);
      return;
    }
    const addBtn = e.target.closest('[data-otf-add]');
    if (addBtn && !addBtn.disabled) {
      addOutfit(addBtn.closest('.otf-modal'), addBtn);
    }
  });

  document.addEventListener('keydown', (e) => {
    if (!openModal) return;
    if (e.key === 'Escape') { close(openModal); return; }
    // Focus trap — keep Tab within the open dialog.
    if (e.key === 'Tab') {
      const f = focusable(openModal);
      if (!f.length) return;
      const first = f[0];
      const last = f[f.length - 1];
      if (!openModal.contains(document.activeElement)) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  });
})();
